import React from 'react';
import { MapPin, Plus, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export const COVERAGE_RADIUS_OPTIONS = [25, 50, 75, 100, 200];
export const DEFAULT_COVERAGE_RADIUS = 50;

export const getCoverageZip = (value: string): string | null => {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 5 ? digits.slice(0, 5) : null;
};

interface VendorCoverageSearchProps {
  zipCode: string;
  radius: number;
  onZipCodeChange: (zip: string) => void;
  onRadiusChange: (radius: number) => void;
  onClear: () => void;
  isSearching?: boolean;
  resultCount?: number;
  closestDistance?: number;
  errorMessage?: string;
  onAddVendor?: () => void;
  children?: React.ReactNode;
}

const VendorCoverageSearch: React.FC<VendorCoverageSearchProps> = ({
  zipCode,
  radius,
  onZipCodeChange,
  onRadiusChange,
  onClear,
  isSearching = false,
  resultCount,
  closestDistance,
  errorMessage,
  onAddVendor,
  children,
}) => {
  const coverageZip = getCoverageZip(zipCode);
  const showResults = !!coverageZip;

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Enter a zip code to see which vendors are within a radius, or to find areas with no coverage.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        {children}
        <div className="w-40">
          <label htmlFor="coverage-zip" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Coverage zip
          </label>
          <Input
            id="coverage-zip"
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={10}
            value={zipCode}
            onChange={(e) => onZipCodeChange(e.target.value)}
            placeholder="e.g. 45439"
            aria-label="Search vendors within a radius of a zip code"
            className="h-10"
          />
        </div>
        <div className="w-40">
          <label htmlFor="coverage-radius" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Radius
          </label>
          <Select
            value={String(radius)}
            onValueChange={(value) => onRadiusChange(Number(value))}
          >
            <SelectTrigger id="coverage-radius" className="w-40" aria-label="Coverage search radius">
              <SelectValue placeholder="Select radius" />
            </SelectTrigger>
            <SelectContent>
              {COVERAGE_RADIUS_OPTIONS.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option} miles
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {zipCode && (
          <button
            type="button"
            onClick={onClear}
            className="h-10 px-3 inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            aria-label="Clear coverage search"
          >
            <X className="h-4 w-4" />
            Clear
          </button>
        )}
      </div>

      {showResults && (
        <div
          className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 ${
            errorMessage
              ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
              : resultCount === 0
                ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
                : 'border-accent-color-200 bg-accent-color-50 dark:border-accent-color-800 dark:bg-accent-color-900/20'
          }`}
        >
          <div className="flex items-start gap-2 text-sm">
            <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-gray-500 dark:text-gray-400" />
            <div>
              {isSearching && (
                <p className="text-gray-700 dark:text-gray-300">
                  Searching for vendors within {radius} miles of {coverageZip}...
                </p>
              )}
              {!isSearching && errorMessage && (
                <p className="text-red-700 dark:text-red-300">{errorMessage}</p>
              )}
              {!isSearching && !errorMessage && resultCount === 0 && (
                <p className="text-amber-800 dark:text-amber-200">
                  No vendors within {radius} miles of {coverageZip}. This area has no vendor coverage.
                </p>
              )}
              {!isSearching && !errorMessage && resultCount !== undefined && resultCount > 0 && (
                <p className="text-accent-color-900 dark:text-accent-color-100">
                  {resultCount} vendor{resultCount === 1 ? '' : 's'} within {radius} miles of {coverageZip}
                  {typeof closestDistance === 'number' ? `. Closest is ${closestDistance.toFixed(1)} miles.` : '.'}
                </p>
              )}
            </div>
          </div>
          {!isSearching && !errorMessage && resultCount === 0 && onAddVendor && (
            <button
              type="button"
              onClick={onAddVendor}
              className="btn-primary"
            >
              <Plus size={16} className="mr-2" />
              Add Vendor
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default VendorCoverageSearch;
